import apiService from "../apiService";
import { handleApiError, TOKEN_KEY } from "../apiService";

// Additional constants
const USER_KEY = "user";

// Main service object
const marathiService = {
  auth: {
    login: async (credentials) => {
      try {
        const response = await apiService.api.post("/api/auth/login", credentials);
        if (response.data.token) {
          localStorage.setItem(TOKEN_KEY, response.data.token);
          localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));
        }
        return response.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },
    logout: () => {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    },
    getCurrentUser: () => {
      try {
        const userStr = localStorage.getItem(USER_KEY);
        return userStr ? JSON.parse(userStr) : null;
      } catch (error) {
        console.error("Error parsing user data:", error);
        return null;
      }
    },
    isAuthenticated: () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return false;
      try {
        const user = marathiService.auth.getCurrentUser();
        return !!user;
      } catch {
        return false;
      }
    },
    getAuthHeader: () => {
      const token = localStorage.getItem(TOKEN_KEY);
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
  },

  upload: {
    multiple: async (files) => {
      try {
        const formData = new FormData();
        files.forEach(file => {
          formData.append('photos', file);
        });

        const response = await apiService.api.post(
          '/api/marathiblogs/gallery/upload',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...marathiService.auth.getAuthHeader()
            }
          }
        );

        return response.data;
      } catch (error) {
        throw handleApiError(error);
      }
    }
  },

  createCrudService: (endpoint) => ({
    getAll: async (params = {}) => {
      try {
        const response = await apiService.api.get(`/api/marathiblogs/${endpoint}`, {
          params: { ...params },
        });
        return response.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },
    getById: async (id) => {
      try {
        const response = await apiService.api.get(`/api/marathiblogs/${endpoint}/${id}`);
        return response.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },
    create: async (data) => {
      try {
        // Special handling for quotes which have content as a string, not an array
        if (endpoint === 'quotes') {
          const processedData = {
            ...data,
            // Make sure tags/categories are arrays
            categories: Array.isArray(data.categories) ? data.categories : (data.categories ? data.categories.split(',').map(t => t.trim()) : [])
          };
          
          // Send data directly as JSON
          const response = await apiService.api.post(
            `/api/marathiblogs/${endpoint}`,
            processedData,
            {
              headers: {
                'Content-Type': 'application/json',
                ...marathiService.auth.getAuthHeader()
              }
            }
          );
          return response.data;
        } else {
          // Standard processing for other content types
          const processedData = {
            ...data,
            // Make sure content is an array of strings
            content: Array.isArray(data.content) ? data.content : [data.content],
            // Make sure tags is an array
            tags: Array.isArray(data.tags) ? data.tags : (data.tags ? data.tags.split(',').map(t => t.trim()) : [])
          };

          // Send data directly as JSON
          const response = await apiService.api.post(
            `/api/marathiblogs/${endpoint}`,
            processedData,
            {
              headers: {
                'Content-Type': 'application/json',
                ...marathiService.auth.getAuthHeader()
              }
            }
          );
          return response.data;
        }
      } catch (error) {
        throw handleApiError(error);
      }
    },
    update: async (id, data) => {
      try {
        // Special handling for quotes which have content as a string, not an array
        if (endpoint === 'quotes') {
          const processedData = {
            ...data,
            // Make sure categories is an array if provided
            categories: data.categories ? (Array.isArray(data.categories) ? data.categories : data.categories.split(',').map(t => t.trim())) : undefined
          };
          
          // Send data directly as JSON
          const response = await apiService.api.put(
            `/api/marathiblogs/${endpoint}/${id}`,
            processedData,
            {
              headers: {
                'Content-Type': 'application/json',
                ...marathiService.auth.getAuthHeader()
              }
            }
          );
          return response.data;
        } else {
          // Standard processing for other content types
          const processedData = {
            ...data,
            // Make sure content is an array of strings if provided
            content: data.content ? (Array.isArray(data.content) ? data.content : [data.content]) : undefined,
            // Make sure tags is an array if provided
            tags: data.tags ? (Array.isArray(data.tags) ? data.tags : data.tags.split(',').map(t => t.trim())) : undefined
          };
          
          // Send data directly as JSON
          const response = await apiService.api.put(
            `/api/marathiblogs/${endpoint}/${id}`,
            processedData,
            {
              headers: {
                'Content-Type': 'application/json',
                ...marathiService.auth.getAuthHeader()
              }
            }
          );
          return response.data;
        }
      } catch (error) {
        throw handleApiError(error);
      }
    },
    delete: async (id) => {
      try {
        const response = await apiService.api.delete(
          `/api/marathiblogs/${endpoint}/${id}`,
          { headers: marathiService.auth.getAuthHeader() }
        );
        return response.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },
  }),

  // Stats service to fetch Marathi-specific stats
  getStats: async () => {
    try {
      const response = await apiService.api.get("/api/stats");
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Updated visitor stats service using the new MarathiVisits routes
  visits: {
    // Track a new visit
    track: async (visitData) => {
      try {
        const response = await apiService.api.post("/api/marathivisits/track", visitData);
        return response.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },

    // Get total visits count
    getTotal: async () => {
      try {
        const response = await apiService.api.get("/api/marathivisits/total");
        return response.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },

    // Get visits by period (day, week, month, year)
    getByPeriod: async (period = "week") => {
      try {
        const response = await apiService.api.get("/api/marathivisits/by-period", {
          params: { period },
        });
        return response.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },

    // Get visits by page path
    getByPath: async (startDate, endDate) => {
      try {
        const params = {};
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        
        const response = await apiService.api.get("/api/marathivisits/by-path", {
          params,
        });
        return response.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },
  },
};

// Initialize CRUD services for each Marathi endpoint
[
  "articles",
  "blogs",
  "infomatics",
  "quotes",
  "speeches",
  "gallery",
  "videoGallery",
  "subscribers",
].forEach((endpoint) => {
  marathiService[endpoint] = marathiService.createCrudService(endpoint);
});

// Add comments service with additional methods
marathiService.comments = {
  ...marathiService.createCrudService("comments"),

  // Additional custom endpoints for comments
  updateStatus: async (id, status) => {
    try {
      const response = await apiService.api.put(
        `/api/marathiblogs/comments/${id}/status`,
        { status },
        { headers: marathiService.auth.getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getByContentType: async (contentType) => {
    try {
      const response = await apiService.api.get(
        `/api/marathiblogs/comments/type/${contentType}`,
        {
          params: { },
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getByContentId: async (contentType, contentId) => {
    try {
      const response = await apiService.api.get(
        `/api/marathiblogs/comments/${contentType}/${contentId}`,
        {
          params: { },
        }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Export default marathiService
export default marathiService;